// Angular imports
import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

// Translation service import
import { TranslateService } from '@ngx-translate/core';

// Interface imports
import { Article } from 'src/app/interfaces/article';

// Service imports
import { ApplicationService } from 'src/app/services/application.service';
import { ArticleService } from 'src/app/services/article.service';
import { SearchService } from 'src/app/services/search.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { AccountService } from 'src/app/services/account.service';
import { BasketService } from 'src/app/services/basket.service';
import { AlertService } from 'src/app/services/alert.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ApiResponse } from 'src/app/interfaces/api-response';

@Component({
    selector: 'app-recommendations-page',
    templateUrl: './recommendations-page.component.html',
    styleUrls: ['./recommendations-page.component.scss'],
    standalone: false
})
export class RecommendationsPageComponent implements OnInit, OnDestroy, AfterViewInit {
  
  // ViewChild reference to article details component
  @ViewChild('articleDetails') articleDetails: any;
  
  // Articles
  recommendedArticles: Article[] = [];
  selectedArticle: Article | null = null;
  
  // Loading states
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  
  companyGroupCode: string = '';
  leaseRequest: boolean = false;
  searchVisible: boolean = false;
  
  // Active recommendation tab
  activeTab: 'TOP' | 'POPULAR' | 'RECOMMENDED' = 'TOP';

  // Bulk add to basket
  adding: boolean = false;
  selectedArticlesCount: number = 0;
  basketId: number = 0; // 0 means current basket
 
  // Flag to track if view is initialized
  private viewInitialized: boolean = false;

  constructor(
    private articleService: ArticleService,
    private applicationService: ApplicationService,
    private router: Router,
    private searchService: SearchService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private deviceDetector: DeviceDetectorService,
    private accountService: AccountService,
    private basketService: BasketService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.companyGroupCode = this.applicationService.getSelectCompanyGroupCode();
    window['recommendationActiveTab'] = this.activeTab;
    this.trackingService.trackPage('recommendations_' + this.activeTab.toLowerCase());
    this.loadRecommendations();
  }

  ngOnDestroy(): void {
    delete window['recommendationActiveTab'];
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
  }

  /**
   * Switch recommendation tab and reload
   */
  selectTab(type: 'TOP' | 'POPULAR' | 'RECOMMENDED'): void {
    if (this.activeTab === type) { return; }
    this.activeTab = type;
    window['recommendationActiveTab'] = type;
    this.selectedArticle = null;
    this.trackingService.trackPage('recommendations_' + type.toLowerCase());
    this.loadRecommendations();
  }

  /**
   * Load recommended articles for the current dealer
   */
  loadRecommendations(): void {
    this.isLoading = true;
    this.hasError = false;

    // Call the service to get recommendations
    this.articleService.getRecommendations(this.activeTab).subscribe({
      next: (response: any) => {
        this.isLoading = false;

        let articles: Article[] = [];

        if (response?.result?.product_overview_items) {
          // New response format
          articles = response.result.product_overview_items;

          // Merge previously_bought metadata if available
          if (response?.result?.recommended_article?.length) {
            const metaMap = new Map<string, any>(
              response.result.recommended_article.map((r: any) => [r.article_code, r])
            );
            articles = articles.map((a: Article) => ({
              ...a,
              previously_bought: metaMap.get(a.single_article_code)?.previously_bought ?? false,
            }));
          }
        } else if (response?.result) {
          // Legacy response format
          articles = response.result;
        }

        this.recommendedArticles = articles;

        if (this.recommendedArticles.length > 0) {
          this.searchService.getNettPrices(this.recommendedArticles);

          setTimeout(() => {
            if (!this.selectedArticle) {
              this.selectArticle(this.recommendedArticles[0]);
            }
          }, 100);
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.hasError = true;
        this.errorMessage = this.translateService.instant('ERROR_LOADING_RECOMMENDATIONS') 
          || 'Failed to load recommendations. Please try again later.';
        console.error('Error loading recommendations:', error);
      }
    });
  }

  /**
   * Select an article and show its details
   */
  selectArticle(article: Article): void {
    this.selectedArticle = article;
    
    // Set the article details component's modelId and trigger load
    if (this.articleDetails) {
      this.articleDetails.modelId = article.id.toString();
      this.articleDetails.article = { ...article };
      this.articleDetails.selectedArticle = article.single_article_code;
      
      // Trigger article details load
      setTimeout(() => {
        this.articleDetails.getArticleDetails();
        
        // Scroll article details to top
        setTimeout(() => {
          const detailsElement = document.querySelector('.article-details-container');
          if (detailsElement) {
            detailsElement.scrollTop = 0;
            
            // On mobile, also scroll the details into view
            if (this.deviceDetector.isMobile()) {
              detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }, 100);
      }, 0);
    } else {
      console.error('❌ ArticleDetails component not found - ViewChild not initialized');
    }
  }

  /**
   * Navigate to article details page or handle article selection from child component
   */
  showArticleDetails(article: Article, event?: any): void {
    this.selectArticle(article);
  }

  /**
   * Handle add to basket event from article card
   */
  onAddToBasket(article: Article, index: number): void {
    // Track the add to basket action for recommendations
    this.trackingService.trackRecommendationAddToBasket(article);
  }

  /**
   * Retry loading recommendations
   */
  retry(): void {
    this.loadRecommendations();
  }

  /**
   * Toggle article selection
   */
  toggleArticleSelection(article: Article): void {
    // Note: ngModel already handles the toggle, we just need to handle side effects
    
    // Initialize quantity if selected
    if (article['selected'] && !article.quantity) {
      article.quantity = 1;
    }
    
    this.updateSelectedCount();
  }

  /**
   * Update selected articles count
   */
  updateSelectedCount(): void {
    this.selectedArticlesCount = this.recommendedArticles.filter(a => a['selected']).length;
  }

  /**
   * Update article quantity
   */
  onQuantityChange(article: Article): void {
    if (article.quantity < 0) {
      article.quantity = 0;
    }
  }

  /**
   * Add selected articles to basket
   */
  addSelectedToBasket(): void {
    const articleList = [];
    
    for (let i = 0; i < this.recommendedArticles.length; i++) {
      const article = this.recommendedArticles[i];
      if (article['selected'] && article.quantity > 0) {
        articleList.push({
          quantity: article.quantity,
          remarks: article.remarks || '',
          sold: article.sold || false,
          single_article_code: article.single_article_code,
          thumbs: article.thumbs,
          week: article.stock_week,
          year: article.stock_year,
          delivery_week: `${article.stock_year}${article.stock_week<10 ? '0' : ''}${article.stock_week}`,
        });
      }
    }

    if (articleList.length === 0) {
      this.alertService.showDangerNotification(
        this.translateService.instant('NO_ARTICLES_SELECTED') || 'No articles selected'
      );
      return;
    }

    this.adding = true;
    this.basketService
      .addArticlesToBasket(articleList, Number(this.basketId))
      .then((responseData: ApiResponse) => {
        this.alertService.showInfoNotification(
          responseData.result || this.translateService.instant('ARTICLES_ADDED_TO_BASKET')
        );
        
        // Reset selections
        this.recommendedArticles.forEach(article => {
          article['selected'] = false;
          article.quantity = 0;
        });
        
        this.updateSelectedCount();
        this.adding = false;
        
        // Track bulk add
        this.trackingService.trackRecommendationAddToBasket({
          single_article_code: `${articleList.length} articles`,
          description: 'Bulk add from recommendations'
        } as Article);
      })
      .catch((error) => {
        this.adding = false;
        this.alertService.showErrorNotification(
          this.translateService.instant('ERROR_ADDING_TO_BASKET') || 'Error adding articles to basket'
        );
        console.error('Error adding articles to basket:', error);
      });
  }

  /**
   * Handle basket selection change
   */
  onBasketChange(basketId: number): void {
    this.basketId = basketId;
  }
}
